import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Command,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sun,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, setCsrfToken } from "../api";
import { environmentLabel, isProductionEnvironment } from "../environment";
import { NAVIGATION_ITEMS, NAVIGATION_SECTIONS } from "../navigation";
import type { Session } from "../types";
import { CommandPalette } from "./CommandPalette";
import { SupposeLogo } from "./Brand";
import { Avatar, Badge, Button, IconButton } from "./UI";

function readTheme(): "light" | "dark" {
  const stored = localStorage.getItem("suppose-admin-theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function AppShell({ session }: { session: Session }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("suppose-admin-nav-collapsed") === "true");
  const [theme, setTheme] = useState<"light" | "dark">(readTheme);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const environment = environmentLabel(session.environment);
  const production = isProductionEnvironment(session.environment);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("suppose-admin-theme", theme);
  }, [theme]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const signOut = useMutation({
    mutationFn: () => api("/api/auth/sign-out", { method: "POST", body: "{}" }),
    onSettled: async () => {
      setCsrfToken(undefined);
      await queryClient.invalidateQueries({ queryKey: ["session"] });
    },
  });
  const toggleCollapsed = () => {
    setCollapsed((value) => {
      localStorage.setItem("suppose-admin-nav-collapsed", String(!value));
      return !value;
    });
  };
  const navigateTo = useCallback((href: string) => {
    void navigate({ to: href });
  }, [navigate]);
  const activeItem = useMemo(() => NAVIGATION_ITEMS.find((item) => item.exact ? path === item.to : path.startsWith(item.to)), [path]);

  return (
    <div className={`app-shell${collapsed ? " nav-collapsed" : ""}`}>
      <aside className={`sidebar${mobileOpen ? " is-mobile-open" : ""}`}>
        <div className="sidebar-brand">
          <Link to="/" aria-label="Suppose admin home"><SupposeLogo /></Link>
          {!collapsed && <Badge tone="indigo">admin</Badge>}
          <IconButton label="Close navigation" className="mobile-nav-close" onClick={() => setMobileOpen(false)}><X size={18} /></IconButton>
        </div>
        <button type="button" className="sidebar-search" onClick={() => setPaletteOpen(true)}>
          <Search size={16} /><span>Find anything</span><kbd>⌘K</kbd>
        </button>
        <nav className="sidebar-nav" aria-label="Main navigation">
          {NAVIGATION_SECTIONS.map((section) => (
            <div className="nav-section" key={section.label}>
              <div className="nav-label">{section.label}</div>
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    activeOptions={{ exact: item.exact ?? false }}
                    activeProps={{ className: "nav-link is-active" }}
                    inactiveProps={{ className: "nav-link" }}
                    title={collapsed ? item.label : undefined}
                    onClick={() => setMobileOpen(false)}
                  >
                    <Icon size={18} /><span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="admin-chip">
            <Avatar name={session.admin?.name} email={session.admin?.email} image={session.admin?.image} size="small" />
            <span><strong>{session.admin?.name || "Admin"}</strong><small>{session.admin?.role || "operator"}</small></span>
            <IconButton label="Sign out" onClick={() => signOut.mutate()} disabled={signOut.isPending}><LogOut size={16} /></IconButton>
          </div>
          <button type="button" className="collapse-button" onClick={toggleCollapsed}>
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}<span>{collapsed ? "Expand" : "Collapse"}</span>
          </button>
        </div>
      </aside>
      {mobileOpen && <button className="mobile-scrim" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}
      <div className="app-main">
        <header className="topbar">
          <div className="topbar-left">
            <IconButton label="Open navigation" className="mobile-menu" onClick={() => setMobileOpen(true)}><Menu size={19} /></IconButton>
            <div className="topbar-context"><strong>{activeItem?.label ?? "Operations"}</strong></div>
          </div>
          <div className="topbar-actions">
            <Button className="topbar-search" size="small" onClick={() => setPaletteOpen(true)}><Command size={14} /> Search <kbd>⌘K</kbd></Button>
            <IconButton label="Toggle color theme" onClick={() => setTheme((value) => value === "light" ? "dark" : "light")}>{theme === "light" ? <Moon size={18} /> : <Sun size={18} />}</IconButton>
            <span
              className={`environment-pill${production ? " is-production" : " is-nonproduction"}`}
              aria-label={`${environment} environment`}
            ><i /> {environment}</span>
          </div>
        </header>
        <main className="page-content"><Outlet /></main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onNavigate={navigateTo} />
    </div>
  );
}
